import { useCallback, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";

import { useTranslation } from "next-i18next";

import useUpdatePassword from "@/Apollo/hooks/useUpdatePassword";
import { AccountSettingsViewProps } from "@/common/contracts/account.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import {
  AccountSettingsSecuritySettingsSchema,
  AccountSettingsSecuritySettingsSchemaType,
} from "@/common/static/account.static";
import { AccountSettingsWrapper } from "@/components/Account/AccountSettingsWrapper";
import { PasswordStrengthEntry } from "@/components/common/signup-login-wall/PasswordStrengthEntry";
import { ConnectedAssistants } from "@/features/agent-oauth/ConnectedAssistants";
import addToast from "@/toast/addToast";
import { showModal } from "@/ui/Modal2";
import { Button } from "@/ui/cva/Button";
import { Input } from "@/ui/cva/input/Input";
import { InputGroup } from "@/ui/cva/input/InputGroup";
import { InputGroupErrorHelper } from "@/ui/cva/input/InputGroupErrorHelper";
import { InputLabel } from "@/ui/typography/InputLabel";
import { zodResolver } from "@hookform/resolvers/zod";

export const AccountSettingsSecuritySettings = ({
  title,
}: AccountSettingsViewProps) => {
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = useState(false);
  const [updatePassword] = useUpdatePassword();

  const {
    register,
    setValue,
    getValues,
    watch,
    handleSubmit: formHandleSubmit,
    formState: { errors },
  } = useForm<AccountSettingsSecuritySettingsSchemaType>({
    resolver: zodResolver(AccountSettingsSecuritySettingsSchema),
    mode: "onChange",
  });

  const handleError = (errors: FieldErrors) => {
    /** Trigger `form_error` event(s). */
    analyticsService.trackAllFormErrors("account_security", errors);
  };

  const handleSubmit = async (
    data: AccountSettingsSecuritySettingsSchemaType
  ) => {
    setIsBusy(true);

    try {
      await updatePassword({
        variables: {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword1,
        },
      });
      showModal("password-change-success");
    } catch (ex) {
      console.log(
        "AccountSettingsSecurity:handleSubmit",
        (ex as Error).message
      );
      addToast(t("account:your-account-settings-error-saving-security"), {
        appearance: "error",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const newPasswordValue = watch("newPassword1");

  const getErrorStatus = useCallback(
    (key: string) => {
      /** If there is any kind of error. */
      if (errors.newPassword1) {
        return key in errors.newPassword1 ? "invalid" : "valid";
      }

      /* If there is no error, but there is a value entered. */
      if (newPasswordValue) {
        return "valid";
      }
    },
    [newPasswordValue, errors]
  );

  const normalizeValues = () => {
    // "Normalize" values before they are validated.
    const newPass1 = getValues("newPassword1");
    const newPass2 = getValues("newPassword2");

    if (newPass1) {
      setValue("newPassword1", newPass1.trim());
    }
    if (newPass2) {
      setValue("newPassword2", newPass2.trim());
    }
  };

  return (
    <AccountSettingsWrapper title={title}>
      <hr className="border-x-0 border-b border-t-0" />
      <form
        onSubmit={formHandleSubmit(handleSubmit, handleError)}
        className="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-4"
      >
        <InputGroup
          valid={!errors.currentPassword}
          disabled={isBusy}
          label={props => (
            <InputLabel className="text-base" {...props}>
              {t("account:your-account-settings-current-password")}
            </InputLabel>
          )}
          input={props => (
            <Input
              placeholder={t("account:your-account-settings-current-password")}
              {...register("currentPassword")}
              {...props}
            />
          )}
          helper={props => (
            <InputGroupErrorHelper {...props} error={errors.currentPassword} />
          )}
        />
        <div className="col-span-2 grid gap-4 md:grid-cols-2">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-6">
              <div className="flex flex-col">
                <InputGroup
                  disabled={isBusy}
                  valid={!errors.newPassword1}
                  label={props => (
                    <InputLabel {...props} className="text-base">
                      {t("account:your-account-settings-new-password1")}
                    </InputLabel>
                  )}
                  input={props => (
                    <Input
                      type="password"
                      placeholder={t(
                        "account:your-account-settings-new-password1"
                      )}
                      {...register("newPassword1")}
                      {...props}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <PasswordStrengthEntry
                  state={getErrorStatus("passwordMinLength")}
                  message={t("common:auth_new_pass_rule1")}
                />
                <PasswordStrengthEntry
                  state={getErrorStatus("passwordHasDigits")}
                  message={t("common:auth_new_pass_rule2")}
                />
                <PasswordStrengthEntry
                  state={getErrorStatus("passwordLowercase")}
                  message={t("common:auth_new_pass_rule3")}
                />
                <PasswordStrengthEntry
                  state={getErrorStatus("passwordUppercase")}
                  message={t("common:auth_new_pass_rule4")}
                />
              </div>
            </div>
          </div>
          <InputGroup
            valid={!errors.newPassword2}
            disabled={isBusy}
            label={props => (
              <InputLabel className="text-base" {...props}>
                {t("account:your-account-settings-new-password2")}
              </InputLabel>
            )}
            input={props => (
              <Input
                type="password"
                placeholder={t("account:your-account-settings-new-password2")}
                {...register("newPassword2")}
                {...props}
              />
            )}
            helper={props => (
              <InputGroupErrorHelper {...props} error={errors.newPassword2} />
            )}
          />
        </div>
        <Button
          type="submit"
          size="large"
          loading={isBusy}
          className="mt-2 md:justify-self-start"
          onClick={normalizeValues}
        >
          {t("account:your-account-settings-save-changes")}
        </Button>
      </form>
      <ConnectedAssistants />
    </AccountSettingsWrapper>
  );
};
